import React, { useState, useEffect, useRef } from 'react';
import {
  FiX,
  FiPlus,
  FiTrash2,
  FiCopy,
  FiEdit2,
  FiCheck,
  FiMenu,
  FiChevronDown,
  FiChevronUp,
  FiSave,
  FiType,
  FiHash,
  FiCalendar,
  FiClock,
  FiList,
  FiCheckSquare,
  FiCamera,
  FiFileText,
  FiEdit3,
  FiDollarSign,
  FiPhone,
  FiUser,
  FiMapPin,
  FiTruck,
  FiEye,
  FiAlignLeft,
  FiTool,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import Api from '../../../Api';
import Modal from '../../../components/Modal';
import Input from '../../../components/Input';
import Select from '../../../components/Select';
import MultiSelect from '../../../components/MultiSelect';
import Button from '../../../components/Button';
import '../../../components/CompanyForm/style.css';
import './style.css';

// Helper to generate IDs
const generateId = () => Math.random().toString(36).substr(2, 9);

const INSPECTION_TYPES = [
  { value: 'employee', label: 'Funcionário' },
  { value: 'free', label: 'Livre' },
  { value: 'station', label: 'Posto de Serviço' },
  { value: 'vehicle', label: 'Veículo' },
  { value: 'infrastructure', label: 'Infraestrutura' },
  { value: 'others', label: 'Outros' },
];

const QUESTION_TYPES = [
  { value: 'text', label: 'Texto Simples', icon: FiType },
  { value: 'long_text', label: 'Texto Longo', icon: FiAlignLeft },
  { value: 'number', label: 'Número', icon: FiHash },
  { value: 'date', label: 'Data', icon: FiCalendar },
  { value: 'time', label: 'Hora', icon: FiClock },
  { value: 'single_choice', label: 'Lista (única)', icon: FiList },
  { value: 'multiple_choice', label: 'Lista (múltipla)', icon: FiCheckSquare },
  { value: 'photo', label: 'Foto', icon: FiCamera },
  { value: 'document', label: 'Documento', icon: FiFileText },
  { value: 'signature', label: 'Assinatura', icon: FiEdit3 },
  { value: 'monetary', label: 'Monetário', icon: FiDollarSign },
  { value: 'phone', label: 'Telefone', icon: FiPhone },
  { value: 'employee', label: 'Funcionário', icon: FiUser },
  { value: 'station', label: 'Posto de Serviço', icon: FiMapPin },
  { value: 'vehicle', label: 'Veículo', icon: FiTruck },
  { value: 'machinery', label: 'Maquinário', icon: FiTool },
];

const EXECUTOR_TYPES = [
  { value: 'employee', label: 'Funcionário' },
  { value: 'user', label: 'Usuário' },
  //{ value: 'client', label: 'Cliente' },
  //{ value: 'supplier', label: 'Fornecedor' },
  //{ value: 'accountant', label: 'Contador' },
];

const COLORS = [
  '#EF4444', // Red
  '#F97316', // Orange
  '#F59E0B', // Amber
  '#10B981', // Emerald
  '#06B6D4', // Cyan
  '#3B82F6', // Blue
  '#6366F1', // Indigo
  '#8B5CF6', // Violet
  '#EC4899', // Pink
  '#64748B', // Slate
];

export default function InspectionModal({ isOpen, onClose, initialData, onSuccess, defaultCompanyId, companies = [] }) {
  // Basic Info
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [executors, setExecutors] = useState([]);
  const [observations, setObservations] = useState('');
  const [color, setColor] = useState(COLORS[5]); // Default Blue
  const [geolocationRequired, setGeolocationRequired] = useState(false);

  // Structure
  const [sections, setSections] = useState([]);
  
  // Drag and Drop State
  const [draggedQuestion, setDraggedQuestion] = useState(null);
  const [draggedOption, setDraggedOption] = useState(null);
  const [canDragQuestionId, setCanDragQuestionId] = useState(null);

  // Executor Selection State
  const [companyIds, setCompanyIds] = useState([]);
  const [selectedExecutorType, setSelectedExecutorType] = useState('');
  const [selectedExecutorEntity, setSelectedExecutorEntity] = useState([]);
  const [executorOptions, setExecutorOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    // Companies are now passed via props
  }, [isOpen]);

  useEffect(() => {
    // Determine which companies to fetch executors for
    let targetCompanyIds = [];
    if (companyIds.includes('all')) {
        targetCompanyIds = companies.map(c => String(c.id));
    } else {
        targetCompanyIds = companyIds;
    }

    if (selectedExecutorType && targetCompanyIds.length > 0) {
      loadExecutorOptions(selectedExecutorType, targetCompanyIds);
    } else {
      setExecutorOptions([{ value: 'all', label: 'Todos' }]);
    }
  }, [selectedExecutorType, companyIds, companies]);

  async function loadExecutorOptions(type, cids) {
    setLoadingOptions(true);
    setExecutorOptions([]);
    setSelectedExecutorEntity([]);
    
    try {
      let allOptions = [];

      // Helper to find company name
      const getCompanyName = (id) => {
        const c = companies.find(comp => String(comp.id) === String(id));
        return c ? (c.trade_name || c.name) : `Empresa ${id}`;
      };

      for (const cid of cids) {
        let options = [];
        const companyName = getCompanyName(cid);

        switch(type) {
          case 'client':
            const clients = await Api.getCustomers(cid);
            if (Array.isArray(clients)) {
               options = clients.map(c => ({ 
                 value: c.id, 
                 label: `${c.name || c.trade_name} (${companyName})`,
                 companyId: cid
               }));
            }
            break;
          case 'supplier':
            const suppliers = await Api.getSuppliers(cid);
            if (Array.isArray(suppliers)) {
              options = suppliers.map(s => ({ 
                value: s.id, 
                label: `${s.name || s.trade_name} (${companyName})`,
                companyId: cid
              }));
            }
            break;
          case 'employee':
            const employees = await Api.getEmployees(cid);
            if (Array.isArray(employees)) {
              options = employees.map(e => ({ 
                value: e.id, 
                label: `${e.name} (${companyName})`,
                companyId: cid
              }));
            }
            break;
          case 'user':
            // Users might be system-wide or company-specific. 
            // If API returns same users for all CIDs, we might get duplicates.
            // But usually inspection config users are filtered by company.
            const users = await Api.getInspectionConfigUsers(cid); 
            if (Array.isArray(users)) {
              options = users.map(u => ({ 
                value: u.id, 
                label: `${u.name} (${companyName})`,
                companyId: cid 
              }));
            }
            break;
          case 'accountant':
            const accountants = await Api.getAccountants(cid);
            if (Array.isArray(accountants)) {
              options = accountants.map(a => ({ 
                value: a.id, 
                label: `${a.name} (${companyName})`,
                companyId: cid
              }));
            }
            break;
          default:
            break;
        }
        allOptions = [...allOptions, ...options];
      }
      
      setExecutorOptions([{ value: 'all', label: 'TODOS' }, ...allOptions]);

    } catch (error) {
      console.error('Error loading executor options', error);
      setExecutorOptions([{ value: 'all', label: 'TODOS' }]);
    } finally {
      setLoadingOptions(false);
    }
  }

  function handleEntityChange(newValues) {
    // Logic for "Todos" (all) exclusivity
    if (newValues.includes('all')) {
      const wasAll = selectedExecutorEntity.includes('all');
      if (!wasAll) {
        // "Todos" was just selected -> clear others
        setSelectedExecutorEntity(['all']);
        return;
      }
      if (newValues.length > 1) {
        // "Todos" was already selected, but user selected something else -> remove "Todos"
        setSelectedExecutorEntity(newValues.filter(v => v !== 'all'));
        return;
      }
    }
    setSelectedExecutorEntity(newValues);
  }

  function handleAddExecutor() {
    if (!selectedExecutorType || selectedExecutorEntity.length === 0) return;
    
    const typeLabel = EXECUTOR_TYPES.find(t => t.value === selectedExecutorType)?.label;
    
    const newExecutors = [];
    let duplicateCount = 0;

    selectedExecutorEntity.forEach(entityId => {
      // Check for duplicates
      const exists = executors.some(e => e.type === selectedExecutorType && e.entityId === entityId);
      // Note: for strict duplicates we might want to check companyId too if same entityId exists in multiple companies (rare for IDs but possible)
      // But entityId usually is unique DB PK.
      
      if (exists) {
        duplicateCount++;
        return;
      }

      const entityOption = executorOptions.find(o => String(o.value) === String(entityId));
      const entityLabel = entityOption?.label || (entityId === 'all' ? 'Todos' : entityId);
      const companyId = entityOption?.companyId || null;

      newExecutors.push({
        id: generateId(),
        type: selectedExecutorType,
        typeLabel: typeLabel,
        entityId: entityId,
        entityLabel: entityLabel,
        companyId: companyId // Store company binding
      });
    });

    if (newExecutors.length === 0 && duplicateCount > 0) {
      toast.info('Todos os executores selecionados já foram adicionados.');
      return;
    }

    setExecutors([...executors, ...newExecutors]);
    setSelectedExecutorType('');
    setSelectedExecutorEntity([]);
    setExecutorOptions([]);
  }

  function removeExecutor(index) {
    const newList = [...executors];
    newList.splice(index, 1);
    setExecutors(newList);
  }

  useEffect(() => {
    if (initialData) {
      setName(initialData.name);
      setType(initialData.type);
      setExecutors(initialData.executors || []);
      setObservations(initialData.observations || '');
      setColor(initialData.color || COLORS[5]);
      setGeolocationRequired(initialData.geolocation_required || false);
      
      // Normalize sections to handle machinery_select -> machinery
      const normalizedSections = (initialData.sections || []).map(section => ({
        ...section,
        questions: section.questions.map(q => ({
          ...q,
          type: q.type === 'machinery_select' ? 'machinery' : q.type
        }))
      }));
      setSections(normalizedSections);
      
      const initialCompanyIds = initialData.companies 
          ? initialData.companies.map(c => String(c.id)) 
          : (initialData.company_id ? [String(initialData.company_id)] : (defaultCompanyId ? [String(defaultCompanyId)] : []));
      
      // If all companies are selected (based on loaded companies list), we might want to check 'all' logic
      // But simpler to just set the IDs.
      setCompanyIds(initialCompanyIds);
    } else {
      resetForm();
      setCompanyIds(defaultCompanyId ? [String(defaultCompanyId)] : []);
    }
  }, [initialData, isOpen, defaultCompanyId]);

  function resetForm() {
    setName('');
    setType('');
    setExecutors([]);
    setObservations('');
    setColor(COLORS[5]);
    setGeolocationRequired(false);
    setSections([
      {
        id: generateId(),
        title: 'Seção 1',
        questions: [],
      },
    ]);
  }

  function handleCompanyChange(newValues) {
    const allValue = 'all';
    const currentSelected = companyIds;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);

    let nextValues;

    if (isAllSelected && !wasAllSelected) {
      // If user selects "All", we might want to just set "All" or select all IDs?
      // Consistent with InspectionConfig:
      const allIds = companies.map(c => String(c.id));
      nextValues = [allValue, ...allIds];
    } else if (!isAllSelected && wasAllSelected) {
      nextValues = [];
    } else if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter(v => v !== allValue);
      nextValues = remaining;
    } else {
      const specificIds = newValues.filter(v => v !== allValue);
      if (specificIds.length === companies.length && companies.length > 0) {
        nextValues = [allValue, ...specificIds];
      } else {
        nextValues = specificIds;
      }
    }
    setCompanyIds(nextValues);
  }

  // --- Section Actions ---

  function addSection() {
    setSections([
      ...sections,
      {
        id: generateId(),
        title: `Nova Seção`,
        questions: [],
      },
    ]);
  }

  function updateSection(sectionId, field, value) {
    setSections(prev =>
      prev.map(s => (s.id === sectionId ? { ...s, [field]: value } : s))
    );
  }

  function cloneSection(sectionIndex) {
    const sectionToClone = sections[sectionIndex];
    const newSection = {
      ...sectionToClone,
      id: generateId(),
      title: `${sectionToClone.title} (Cópia)`,
      questions: sectionToClone.questions.map(q => ({ ...q, id: generateId() })),
    };
    const newSections = [...sections];
    newSections.splice(sectionIndex + 1, 0, newSection);
    setSections(newSections);
  }

  function deleteSection(sectionIndex) {
    if (sections.length === 1) {
      toast.error('A inspeção deve ter pelo menos uma seção.');
      return;
    }
    const newSections = [...sections];
    newSections.splice(sectionIndex, 1);
    setSections(newSections);
  }

  // --- Question Actions ---

  function addQuestion(sectionId) {
    const newQuestion = {
      id: generateId(),
      text: '',
      type: 'text',
      required: false,
      options: [],
      conditional: { enabled: false, referenceId: '', expectedValue: '' },
    };

    setSections(prev =>
      prev.map(s =>
        s.id === sectionId ? { ...s, questions: [...s.questions, newQuestion] } : s
      )
    );
  }

  function updateQuestion(sectionId, questionId, field, value) {
    setSections(prev => {
      // 1. Update the specific question first
      const updatedSections = prev.map(s => {
        if (s.id !== sectionId) return s;
        return {
          ...s,
          questions: s.questions.map(q =>
            q.id === questionId ? { ...q, [field]: value } : q
          ),
        };
      });

      // 2. If type changed, reset expectedValue for any question that references this one
      if (field === 'type') {
        return updatedSections.map(s => ({
          ...s,
          questions: s.questions.map(q => {
            // Check if this question depends on the one being updated
            if (q.conditional?.referenceId === questionId) {
              return {
                ...q,
                conditional: {
                  ...q.conditional,
                  expectedValue: '' // Reset expected value as type changed
                }
              };
            }
            return q;
          })
        }));
      }

      return updatedSections;
    });
  }

  function cloneQuestion(sectionId, questionIndex) {
    setSections(prev =>
      prev.map(s => {
        if (s.id !== sectionId) return s;
        const questionToClone = s.questions[questionIndex];
        const newQuestion = {
          ...questionToClone,
          id: generateId(),
          text: `${questionToClone.text} (Cópia)`,
        };
        const newQuestions = [...s.questions];
        newQuestions.splice(questionIndex + 1, 0, newQuestion);
        return { ...s, questions: newQuestions };
      })
    );
  }

  function deleteQuestion(sectionId, questionIndex) {
    setSections(prev =>
      prev.map(s => {
        if (s.id !== sectionId) return s;
        const newQuestions = [...s.questions];
        newQuestions.splice(questionIndex, 1);
        return { ...s, questions: newQuestions };
      })
    );
  }

  // --- Options Logic (for Single/Multiple Choice) ---

  function addOption(sectionId, questionId) {
    const newOption = { id: generateId(), text: '' };
    setSections(prev =>
      prev.map(s => {
        if (s.id !== sectionId) return s;
        return {
          ...s,
          questions: s.questions.map(q =>
            q.id === questionId
              ? { ...q, options: [...(q.options || []), newOption] }
              : q
          ),
        };
      })
    );
  }

  function updateOption(sectionId, questionId, optionId, value) {
    setSections(prev =>
      prev.map(s => {
        if (s.id !== sectionId) return s;
        return {
          ...s,
          questions: s.questions.map(q => {
            if (q.id !== questionId) return q;
            return {
              ...q,
              options: q.options.map(opt =>
                opt.id === optionId ? { ...opt, text: value } : opt
              ),
            };
          }),
        };
      })
    );
  }

  function deleteOption(sectionId, questionId, optionIndex) {
    setSections(prev =>
      prev.map(s => {
        if (s.id !== sectionId) return s;
        return {
          ...s,
          questions: s.questions.map(q => {
            if (q.id !== questionId) return q;
            const newOptions = [...q.options];
            newOptions.splice(optionIndex, 1);
            return { ...q, options: newOptions };
          }),
        };
      })
    );
  }

  // Helper to validate conditions after reorder
  function validateSectionQuestions(questions) {
    return questions.map((q, index) => {
      if (!q.conditional?.enabled || !q.conditional?.referenceId) return q;
      
      const refIndex = questions.findIndex(rq => rq.id === q.conditional.referenceId);
      
      // If reference is below or same as current question (must be strictly before)
      if (refIndex === -1 || refIndex >= index) {
        return {
          ...q,
          conditional: { ...q.conditional, referenceId: '', expectedValue: '' }
        };
      }
      return q;
    });
  }

  // --- Drag and Drop Logic (Questions) ---

  function handleDragStart(e, sectionId, index) {
    setDraggedQuestion({ sectionId, index });
    e.dataTransfer.effectAllowed = 'move';
    // Add visual class
    e.target.classList.add('dragging');
  }

  function handleDragOver(e, sectionId, index) {
    e.preventDefault();
    if (!draggedQuestion) return;
    if (draggedQuestion.sectionId !== sectionId) return; // Only allow sort within same section for now
    if (draggedQuestion.index === index) return;

    // Perform swap immediately
    const dragIndex = draggedQuestion.index;
    const dropIndex = index;

    if (document.startViewTransition) {
      // document.startViewTransition removed to prevent modal overflow/z-index issues
      setSections(prev =>
        prev.map(s => {
          if (s.id !== sectionId) return s;
          const newQuestions = [...s.questions];
          const [movedItem] = newQuestions.splice(dragIndex, 1);
          newQuestions.splice(dropIndex, 0, movedItem);
          
          // Validate conditions after reorder
          const validatedQuestions = validateSectionQuestions(newQuestions);
          
          return { ...s, questions: validatedQuestions };
        })
      );
    } else {
      setSections(prev =>
        prev.map(s => {
          if (s.id !== sectionId) return s;
          const newQuestions = [...s.questions];
          const [movedItem] = newQuestions.splice(dragIndex, 1);
          newQuestions.splice(dropIndex, 0, movedItem);

          // Validate conditions after reorder
          const validatedQuestions = validateSectionQuestions(newQuestions);

          return { ...s, questions: validatedQuestions };
        })
      );
    }
    
    // Update dragged index to match the new position
    setDraggedQuestion({ ...draggedQuestion, index: dropIndex });
  }

  function handleDrop(e, sectionId, dropIndex) {
    e.preventDefault();
    const draggedEl = document.querySelector('.dragging');
    if (draggedEl) draggedEl.classList.remove('dragging');
    setDraggedQuestion(null);
  }

  function handleDragEnd(e) {
    e.target.classList.remove('dragging');
    setDraggedQuestion(null);
  }

  // --- Drag and Drop Logic (Options) ---

  function handleOptionDragStart(e, sectionId, questionId, index) {
    e.stopPropagation(); // Prevent bubbling to question drag
    setDraggedOption({ sectionId, questionId, index });
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.classList.add('dragging');
  }

  function handleOptionDragOver(e, sectionId, questionId, index) {
    e.preventDefault();
    e.stopPropagation();
    if (!draggedOption) return;
    if (draggedOption.sectionId !== sectionId || draggedOption.questionId !== questionId) return;
    if (draggedOption.index === index) return;

    // Perform swap immediately
    const dragIndex = draggedOption.index;
    const dropIndex = index;

    if (document.startViewTransition) {
      // document.startViewTransition removed to prevent modal overflow/z-index issues
      setSections(prev =>
        prev.map(s => {
          if (s.id !== sectionId) return s;
          return {
            ...s,
            questions: s.questions.map(q => {
              if (q.id !== questionId) return q;
              const newOptions = [...q.options];
              const [movedItem] = newOptions.splice(dragIndex, 1);
              newOptions.splice(dropIndex, 0, movedItem);
              return { ...q, options: newOptions };
            }),
          };
        })
      );
    } else {
      setSections(prev =>
        prev.map(s => {
          if (s.id !== sectionId) return s;
          return {
            ...s,
            questions: s.questions.map(q => {
              if (q.id !== questionId) return q;
              const newOptions = [...q.options];
              const [movedItem] = newOptions.splice(dragIndex, 1);
              newOptions.splice(dropIndex, 0, movedItem);
              return { ...q, options: newOptions };
            }),
          };
        })
      );
    }
    
    // Update dragged index to match the new position
    setDraggedOption({ ...draggedOption, index: dropIndex });
  }

  function handleOptionDrop(e, sectionId, questionId, dropIndex) {
    e.preventDefault();
    e.stopPropagation();
    const draggedEl = document.querySelector('.inspection-config-option-item.dragging');
    if (draggedEl) draggedEl.classList.remove('dragging');
    setDraggedOption(null);
  }

  function handleOptionDragEnd(e) {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget) e.currentTarget.classList.remove('dragging');
    setDraggedOption(null);
  }

  function renderConditionalInput(section, question) {
    const referenceId = question.conditional?.referenceId;
    
    // If no reference selected, show disabled input
    if (!referenceId) {
       return (
         <Input
           placeholder="Selecione uma pergunta"
           disabled
           noMargin
           size="sm"
         />
       );
    }

    const refQuestion = section.questions.find(q => q.id === referenceId);
    
    // If reference question not found (shouldn't happen), fallback to text
    if (!refQuestion) {
        return (
             <Input
               placeholder="Valor esperado"
               value={question.conditional?.expectedValue || ''}
               onChange={e => updateQuestion(section.id, question.id, 'conditional', { ...question.conditional, expectedValue: e.target.value })}
               noMargin
               size="sm"
             />
        );
    }

    const value = question.conditional?.expectedValue || '';
    const handleChange = (val) => {
       updateQuestion(section.id, question.id, 'conditional', { 
         ...question.conditional, 
         expectedValue: val 
       });
    };

    switch (refQuestion.type) {
      case 'multiple_choice':
        const multiOptions = refQuestion.options?.map(opt => ({ 
            value: opt.text, 
            label: opt.text 
        })) || [];
        
        // Ensure value is array for multiple select
        const multiValue = Array.isArray(value) ? value : (value ? [value] : []);
        
        return (
          <Select
            options={multiOptions}
            value={multiValue}
            onChange={(e) => handleChange(e.target.value)}
            placeholder="Selecione as opções"
            noMargin
            size="sm"
            useCustomDropdown
            multiple
          />
        );

      case 'single_choice':
        const options = refQuestion.options?.map(opt => ({ 
            value: opt.text, 
            label: opt.text 
        })) || [];
        
        return (
          <Select
            options={options}
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            placeholder="Selecione a opção"
            noMargin
            size="sm"
            useCustomDropdown
          />
        );

      case 'date':
        return (
          <Input
             mask="date"
             placeholder="00/00/0000"
             value={value}
             onChange={(e) => handleChange(e.target.value)}
             noMargin
             size="sm"
          />
        );

      case 'time':
        return (
          <Input
             type="time"
             value={value}
             onChange={(e) => handleChange(e.target.value)}
             noMargin
             size="sm"
          />
        );

      case 'phone':
        return (
          <Input
             mask="phone"
             placeholder="(00) 00000-0000"
             value={value}
             onChange={(e) => handleChange(e.target.value)}
             noMargin
             size="sm"
          />
        );

      case 'monetary':
        return (
           <Input
             mask="currency"
             placeholder="R$ 0,00"
             value={value}
             onChange={(e) => handleChange(e.target.value)}
             noMargin
             size="sm"
          />
        );

      case 'number':
        return (
           <Input
             type="number"
             value={value}
             onChange={(e) => handleChange(e.target.value)}
             placeholder="Valor numérico"
             noMargin
             size="sm"
          />
        );

      case 'photo':
      case 'document':
      case 'signature':
      case 'employee':
      case 'station':
      case 'vehicle':
      case 'text':
      case 'long_text':
        return (
          <Select
            options={[
              { value: 'contem', label: 'Contém' },
              { value: 'nao_contem', label: 'Não Contém' }
            ]}
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            placeholder="SELECIONE..."
            noMargin
            size="sm"
            useCustomDropdown
          />
        );

      default:
        return (
           <Input
             placeholder="Valor esperado"
             value={value}
             onChange={(e) => handleChange(e.target.value)}
             noMargin
             size="sm"
           />
        );
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!name) {
      toast.error('Nome da inspeção é obrigatório.');
      return;
    }
    if (!type) {
      toast.error('Tipo da inspeção é obrigatório.');
      return;
    }
    if (companyIds.length === 0) {
      toast.error('Selecione pelo menos uma empresa.');
      return;
    }

    // Validation: Check if questions have text
    for (const section of sections) {
      for (const question of section.questions) {
        if (!question.text.trim()) {
          toast.error(`Existem perguntas sem texto na seção "${section.title}".`);
          return;
        }
        if (['single_choice', 'multiple_choice'].includes(question.type)) {
          if (!question.options || question.options.length === 0) {
            toast.error(`A pergunta "${question.text}" precisa de opções.`);
            return;
          }
        }
      }
    }

    const payload = {
      name,
      type,
      executors,
      observations,
      color,
      geolocation_required: geolocationRequired,
      sections,
    };

    onSuccess(payload, companyIds);
    onClose();
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? 'Editar Inspeção' : 'Cadastro da Inspeção'}
      size="xl" // Larger modal for form builder
      closeOnOverlayClick={false}
    >
      <form onSubmit={handleSubmit} className="company-form-component">
        <div className="form-body">
        {/* 1. Basic Info */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 className="section-title">Informações Básicas</h3>
          <div className="form-grid">
            <div>
              <Select
                label="Empresa"
                options={[
                  { value: 'all', label: 'TODAS' },
                  ...(companies.map(c => ({
                    value: String(c.id),
                    label: c.corporate_name || c.trade_name || c.name || `Empresa ${c.id}`
                  })))
                ]}
                value={companyIds}
                onChange={(e) => handleCompanyChange(e.target.value)}
                placeholder="SELECIONE..."
                searchable
                multiple={true}
              />
            </div>
            <div>
              <Input
                label="Nome da Inspeção"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Ex: Checklist Veicular"
                required
                autoFocus
              />
            </div>
            <div>
              <Select
                label="Tipo da Inspeção"
                options={INSPECTION_TYPES}
                value={type}
                onChange={e => setType(e.target.value)}
                placeholder="SELECIONE..."
                required
                useCustomDropdown
              />
            </div>
            <div>
              <Select
                label="Geolocalização obrigatória"
                options={[
                  { value: 'false', label: 'Não' },
                  { value: 'true', label: 'Sim' }
                ]}
                value={geolocationRequired ? 'true' : 'false'}
                onChange={e => setGeolocationRequired(e.target.value === 'true')}
                placeholder="SELECIONE..."
                useCustomDropdown
              />
            </div>
          </div>
        </div>

        {/* 2. Sections Builder */}
        <div className="inspection-config-sections-container">
          <div>
            <h3 className="section-title" style={{ marginBottom: 0 }}>Estrutura do Checklist</h3>
          </div>

          {sections.map((section, sIndex) => (
            <div key={section.id} className="inspection-config-section-card">
              <div className="inspection-config-section-header">
                <div className="inspection-section-title-wrapper">
                  <Input
                    value={section.title}
                    onChange={e => updateSection(section.id, 'title', e.target.value)}
                    placeholder="Título da Seção"
                    noMargin
                  />
                </div>
                <div className="inspection-config-question-actions">
                  <Button
                    type="button"
                    variant="outline"
                    startIcon={<FiCopy />}
                    onClick={() => cloneSection(sIndex)}
                    title="Clonar Seção"
                  />
                  <Button
                    type="button"
                    variant="danger"
                    startIcon={<FiTrash2 />}
                    onClick={() => deleteSection(sIndex)}
                    title="Excluir Seção"
                  />
                </div>
              </div>

              {/* Questions List */}
              <div className="inspection-config-questions-list">
                {section.questions.map((question, qIndex) => (
                  <div
                    key={question.id}
                    className="inspection-config-question-card"
                    draggable={canDragQuestionId === question.id}
                    onDragStart={e => handleDragStart(e, section.id, qIndex)}
                    onDragOver={e => handleDragOver(e, section.id, qIndex)}
                    onDrop={e => handleDrop(e, section.id, qIndex)}
                    onDragEnd={handleDragEnd}
                  >
                    <div className="inspection-config-question-header">
                      <div 
                        className="inspection-config-drag-handle" 
                        title="Arraste para reordenar"
                        onMouseEnter={() => setCanDragQuestionId(question.id)}
                        onMouseLeave={() => setCanDragQuestionId(null)}
                        onTouchStart={() => setCanDragQuestionId(question.id)}
                      >
                        <FiMenu size={20} />
                      </div>
                      <div className="inspection-question-inputs-wrapper">
                         <div className="inspection-question-text-wrapper">
                           <Input
                             value={question.text}
                             onChange={e => updateQuestion(section.id, question.id, 'text', e.target.value)}
                             placeholder="Texto da Pergunta"
                             noMargin
                           />
                         </div>
                         <div className="inspection-question-type-wrapper">
                           <Select
                             options={QUESTION_TYPES}
                             value={question.type}
                             onChange={e => updateQuestion(section.id, question.id, 'type', e.target.value)}
                             noMargin
                             useCustomDropdown
                           />
                         </div>
                      </div>
                      <div className="inspection-config-question-actions">
                         <Button
                           type="button"
                           variant="outline"
                           startIcon={<FiCopy />}
                           onClick={() => cloneQuestion(section.id, qIndex)}
                           size="sm"
                         />
                         <Button
                           type="button"
                           variant="danger"
                           startIcon={<FiTrash2 />}
                           onClick={() => deleteQuestion(section.id, qIndex)}
                           size="sm"
                         />
                      </div>
                    </div>

                    <div className="inspection-config-question-body">
                      {/* Configuration Row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
                         <label className="inspection-config-executor-checkbox" style={{ width: 'auto', border: 'none', padding: 0 }}>
                           <input
                             type="checkbox"
                             checked={question.required}
                             onChange={e => updateQuestion(section.id, question.id, 'required', e.target.checked)}
                             style={{ marginRight: '0.5rem' }}
                           />
                           Obrigatória
                         </label>

                         {/* Conditional Logic Toggle */}
                         <label className="inspection-config-executor-checkbox" style={{ width: 'auto', border: 'none', padding: 0 }}>
                            <input
                              type="checkbox"
                              checked={question.conditional?.enabled}
                              onChange={e => updateQuestion(section.id, question.id, 'conditional', { ...question.conditional, enabled: e.target.checked })}
                              style={{ marginRight: '0.5rem' }}
                            />
                            Condicional
                         </label>
                      </div>

                      {/* Options Editor (for Choice types) */}
                      {['single_choice', 'multiple_choice'].includes(question.type) && (
                        <div className="inspection-config-options-list">
                          <label className="input-label" style={{ fontSize: '0.85rem' }}>Opções de Resposta</label>
                          {question.options?.map((option, optIndex) => (
                            <div
                              key={option.id}
                              className="inspection-config-option-item"
                              draggable
                              onDragStart={e => handleOptionDragStart(e, section.id, question.id, optIndex)}
                              onDragOver={e => handleOptionDragOver(e, section.id, question.id, optIndex)}
                              onDrop={e => handleOptionDrop(e, section.id, question.id, optIndex)}
                              onDragEnd={handleOptionDragEnd}
                            >
                              <div className="inspection-config-drag-handle">
                                <FiMenu size={14} />
                              </div>
                              <Input
                                value={option.text}
                                onChange={e => updateOption(section.id, question.id, option.id, e.target.value)}
                                placeholder={`Opção ${optIndex + 1}`}
                                noMargin
                                size="sm"
                              />
                              <Button
                                type="button"
                                variant="danger"
                                startIcon={<FiX />}
                                onClick={() => deleteOption(section.id, question.id, optIndex)}
                                size="sm"
                                isIconOnly
                              />
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            startIcon={<FiPlus />}
                            onClick={() => addOption(section.id, question.id)}
                            size="sm"
                            style={{ alignSelf: 'flex-start', marginTop: '0.5rem' }}
                          >
                            Adicionar Opção
                          </Button>
                        </div>
                      )}

                      {/* Conditional Logic Panel */}
                      {question.conditional?.enabled && (
                        <div className="inspection-config-conditional-logic-panel">
                          <strong>Lógica Condicional:</strong> Exibir esta pergunta apenas se...
                          <div className="inspection-conditional-grid">
                             <Select
                               placeholder="Pergunta de referência"
                               options={section.questions
                                 .slice(0, qIndex) // Only show previous questions
                                 .map(q => ({ value: q.id, label: q.text || '(Sem texto)' }))
                               }
                               value={question.conditional.referenceId}
                               onChange={e => updateQuestion(section.id, question.id, 'conditional', { ...question.conditional, referenceId: e.target.value })}
                               noMargin
                               size="sm"
                               useCustomDropdown
                             />
                             {renderConditionalInput(section, question)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                <Button
                  type="button"
                  variant="outline"
                  startIcon={<FiPlus />}
                  onClick={() => addQuestion(section.id)}
                  style={{ marginTop: '1rem' }}
                >
                  Adicionar Pergunta
                </Button>
              </div>
            </div>
          ))}

          <Button
            type="button"
            variant="primary"
            startIcon={<FiPlus />}
            onClick={addSection}
            className="inspection-config-full-width"
            style={{ padding: '1rem' }}
          >
            Adicionar Nova Seção
          </Button>
        </div>

        {/* 3. Additional Config */}
        <div style={{ marginTop: '2rem' }}>
          <div>
            <h3 className="section-title">Configurações Adicionais</h3>
          </div>
          
          <div className="inspection-config-form-grid" style={{ gridTemplateColumns: '1fr' }}>
          <div className="inspection-config-full-width">
            <label className="input-label" style={{ marginBottom: '1rem', display: 'block' }}>Executores Permitidos</label>
            
            <div className="inspection-executors-grid">
              <Select
                label="Tipo"
                options={EXECUTOR_TYPES}
                value={selectedExecutorType}
                onChange={e => setSelectedExecutorType(e.target.value)}
                placeholder="Selecione o tipo..."
                useCustomDropdown
                noMargin
              />
              <MultiSelect
                label="Quem?"
                options={executorOptions}
                value={selectedExecutorEntity}
                onChange={handleEntityChange}
                placeholder={loadingOptions ? "Carregando..." : "SELECIONE..."}
                disabled={!selectedExecutorType || loadingOptions}
              />
              <Button
                type="button"
                onClick={handleAddExecutor}
                disabled={!selectedExecutorType || selectedExecutorEntity.length === 0}
                style={{ height: '42px' }} 
                startIcon={<FiPlus />}
              >
                Adicionar
              </Button>
            </div>

            <div className="added-executors-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', minHeight: '40px', padding: '0.5rem', backgroundColor: '#f8fafc', borderRadius: '0.5rem', border: '1px dashed #e2e8f0' }}>
              {executors.map((executor, index) => {
                const isLegacy = typeof executor === 'string';
                const type = isLegacy ? executor : executor.type;
                const typeLabel = isLegacy 
                    ? EXECUTOR_TYPES.find(t => t.value === executor)?.label 
                    : (executor.typeLabel || EXECUTOR_TYPES.find(t => t.value === executor.type)?.label);
                const entityLabel = isLegacy ? 'Todos' : (executor.entityLabel || (executor.entityId === 'all' ? 'Todos' : executor.entityId));

                return (
                <div
                  key={executor.id || index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.25rem 0.75rem',
                    backgroundColor: '#fff',
                    borderRadius: '20px',
                    border: '1px solid #e2e8f0',
                    fontSize: '0.85rem',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                  }}
                >
                  <span style={{ fontWeight: 600, color: '#64748b' }}>{typeLabel}:</span>
                  <span style={{ color: '#334155' }}>{entityLabel}</span>
                  <button
                    type="button"
                    onClick={() => removeExecutor(index)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#ef4444',
                      padding: 0,
                      marginLeft: '0.25rem'
                    }}
                  >
                    <FiX size={14} />
                  </button>
                </div>
                );
              })}
              {executors.length === 0 && (
                <span style={{ color: '#94a3b8', fontSize: '0.9rem', padding: '0.25rem' }}>
                  Nenhum executor adicionado.
                </span>
              )}
            </div>
          </div>

          <div className="inspection-config-full-width">
            <Input
              label="Observações"
              value={observations}
              onChange={e => setObservations(e.target.value)}
              placeholder="Informações adicionais..."
              multiline
            />
          </div>

          <div className="inspection-config-full-width" style={{ marginTop: '1rem' }}>
            <label className="input-label">Cor de Identificação</label>
            <div className="inspection-config-color-picker-wrapper">
              {COLORS.map(c => (
                <div
                  key={c}
                  className={`inspection-config-color-circle ${color === c ? 'selected' : ''}`}
                  style={{ backgroundColor: c }}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
          </div>
          </div>
        </div>

        </div>
        <div className="form-actions">
          <Button type="button" className="cancel-button" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary">
            Salvar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
